/**
 * UploadPortalsPage — tenant-side Client Upload Portals.
 *
 * Reached from the cloud icon on a task or sub-task row:
 *   - no portals yet  → the "Create Public Upload Portal for [name]" form
 *   - portals exist   → the Portal Management Window
 *
 * Management shows each portal with its public link underneath the title
 * (click to copy), a right-click context menu on the title (Rename /
 * Close-Reopen Portal), a zip-all download, the file grid (type icons,
 * click to open, download, eye = uploader attribution + virus status,
 * delete with mandatory reason), the recycle bin, and New Portal.
 *
 * All mutations flow through the SITAC-backed API; edits require a reason.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Button, IconButton, Input, Textarea, Select,
  Checkbox, Badge, Tooltip, useToast, FormControl, FormLabel, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, Tag,
} from '@chakra-ui/react';
import {
  FiUploadCloud, FiPlus, FiTrash2, FiDownload, FiEye, FiCopy, FiFile,
  FiFileText, FiImage, FiFilm, FiMusic, FiArchive, FiRotateCcw, FiLock, FiEyeOff,
  FiUnlock, FiX, FiExternalLink, FiShield, FiAlertTriangle, FiMail, FiSlash, FiBriefcase,
} from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';
import UserTransferList from './UserTransferList';
import ConfirmDialog from './ConfirmDialog';

function fileIcon(mime, name) {
  const n = String(name || '').toLowerCase();
  const m = String(mime || '');
  if (m.startsWith('image/')) return <FiImage size={26} />;
  if (m.startsWith('video/')) return <FiFilm size={26} />;
  if (m.startsWith('audio/')) return <FiMusic size={26} />;
  if (m.includes('pdf') || n.endsWith('.pdf')) return <FiFileText size={26} color="#c53030" />;
  if (/\.(zip|rar|7z|tar|gz)$/.test(n)) return <FiArchive size={26} />;
  if (/\.(doc|docx|odt|txt|rtf)$/.test(n)) return <FiFileText size={26} color="#2b6cb0" />;
  if (/\.(xls|xlsx|csv|ods)$/.test(n)) return <FiFileText size={26} color="#2f855a" />;
  return <FiFile size={26} />;
}

function virusBadge(status) {
  if (status === 'clean') return <Badge colorScheme="green" fontSize="8px">VIRUS CHECK: CLEAN</Badge>;
  if (status === 'infected') return <Badge colorScheme="red" fontSize="8px">VIRUS DETECTED</Badge>;
  if (status === 'scanner_unavailable') return <Badge colorScheme="orange" fontSize="8px">CHECK ENABLED — SCANNER NOT PROVISIONED</Badge>;
  return <Badge colorScheme="gray" fontSize="8px">NOT VIRUS CHECKED</Badge>;
}

// ── Portal create form (page mode for first portal; modal for additional) ───
function PortalForm({ contextLabel, standardFields, securityOptions, users, selfId, selfName, onSubmit, onCancel, busy }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const opts = securityOptions || { link: true, email: true, password: true, mfa: true };
  const [accessMode, setAccessMode] = useState(opts.email ? 'email' : (opts.password ? 'password' : 'link'));
  const [requireMfa, setRequireMfa] = useState(false);
  const [visibilityScope, setVisibilityScope] = useState('all');
  const [downloadScope, setDownloadScope] = useState('all');
  const [internalUsers, setInternalUsers] = useState([]);
  const [password, setPassword] = useState('');
  const [emails, setEmails] = useState('');
  const [fields, setFields] = useState(
    (standardFields || []).map((f, i) => ({ ...f, enabled: f.field_key === 'description', is_required: f.field_key === 'description', sort: i })));
  const [customs, setCustoms] = useState([]);

  const toggleStd = (key, prop, val) =>
    setFields(fs => fs.map(f => f.field_key === key ? { ...f, [prop]: val } : f));
  const addCustom = () => setCustoms(cs => [...cs, { label: '', field_type: 'text', tooltip: '', is_required: false }]);
  const setCustom = (i, prop, val) => setCustoms(cs => cs.map((c, j) => j === i ? { ...c, [prop]: val } : c));

  const submit = () => {
    const fieldPayload = [
      ...fields.filter(f => f.enabled).map(f => ({
        field_key: f.field_key, label: f.label, field_type: f.field_type,
        tooltip: f.tooltip, is_required: !!f.is_required, is_standard: true,
      })),
      ...customs.filter(c => c.label.trim()).map(c => ({
        label: c.label.trim(), field_type: c.field_type, tooltip: c.tooltip.trim() || null,
        is_required: !!c.is_required, is_standard: false,
      })),
    ];
    onSubmit({
      name: name.trim(), description: description.trim() || undefined, access_mode: accessMode,
      require_mfa: accessMode === 'email' ? requireMfa : false,
      visibility_scope: visibilityScope, download_scope: downloadScope,
      internal_user_ids: (visibilityScope === 'specified' || downloadScope === 'specified') ? internalUsers.map(u => u.id) : [],
      password: accessMode === 'password' ? password : undefined,
      emails: accessMode === 'email' ? emails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean) : [],
      fields: fieldPayload,
    });
  };

  return (
    <VStack align="stretch" spacing={4} maxW="760px">
      <FormControl isRequired>
        <FormLabel fontSize="sm">Folder name (shown to the public)</FormLabel>
        <Input size="sm" value={name} onChange={e => setName(e.target.value)} placeholder={`e.g. Documents for ${contextLabel || 'this work'}`} />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Friendly description (shown to the public under the title)</FormLabel>
        <Textarea size="sm" rows={2} value={description} onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Please upload your signed contract and any supporting documents here." maxLength={500} />
      </FormControl>

      <Box>
        <Text fontSize="12px" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
          Information requested with each file
        </Text>
        <Text fontSize="11px" color="gray.500" mb={2}>
          Tick the standard fields to request, mark whether each is compulsory, and add your own fields below.
          The public sees these as "Information about this file", with your tooltip shown as guidance.
        </Text>
        <VStack align="stretch" spacing={1.5}>
          {fields.map(f => (
            <Flex key={f.field_key} gap={3} align="center" p={2} border="1px" borderColor="gray.100" borderRadius="md">
              <Checkbox size="sm" isChecked={!!f.enabled} onChange={e => toggleStd(f.field_key, 'enabled', e.target.checked)} minW="180px">
                <Text fontSize="13px">{f.label}</Text>
              </Checkbox>
              <Text fontSize="10px" color="gray.400" flex={1} noOfLines={1}>{f.tooltip}</Text>
              {!!f.enabled && (
                <Checkbox size="sm" isChecked={!!f.is_required} onChange={e => toggleStd(f.field_key, 'is_required', e.target.checked)}>
                  <Text fontSize="11px">Compulsory</Text>
                </Checkbox>
              )}
            </Flex>
          ))}
        </VStack>

        <Button size="xs" mt={2} leftIcon={<FiPlus size={11} />} variant="outline" onClick={addCustom}>
          Add custom field
        </Button>
        {customs.map((c, i) => (
          <Flex key={i} gap={2} mt={2} align="flex-end" flexWrap="wrap" p={2} bg="gray.50" borderRadius="md">
            <FormControl w="180px"><FormLabel fontSize="10px" mb={0.5}>Field name</FormLabel>
              <Input size="xs" value={c.label} onChange={e => setCustom(i, 'label', e.target.value)} /></FormControl>
            <FormControl w="110px"><FormLabel fontSize="10px" mb={0.5}>Type</FormLabel>
              <Select size="xs" value={c.field_type} onChange={e => setCustom(i, 'field_type', e.target.value)}>
                <option value="text">Text</option><option value="textarea">Description</option>
                <option value="checkbox">Tick box</option><option value="date">Date</option>
              </Select></FormControl>
            <FormControl flex={1} minW="200px"><FormLabel fontSize="10px" mb={0.5}>Tooltip (help for the public)</FormLabel>
              <Input size="xs" value={c.tooltip} onChange={e => setCustom(i, 'tooltip', e.target.value)} /></FormControl>
            <Checkbox size="sm" isChecked={!!c.is_required} onChange={e => setCustom(i, 'is_required', e.target.checked)}>
              <Text fontSize="11px">Compulsory</Text></Checkbox>
            <IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiX size={11} />} aria-label="remove"
              onClick={() => setCustoms(cs => cs.filter((_, j) => j !== i))} />
          </Flex>
        ))}
      </Box>

      <Box>
        <Text fontSize="12px" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={2}>
          Who can upload
        </Text>
        {/* Only modes the ForgeBOS platform administrator has enabled
            are offered; the API enforces this server-side regardless. */}
        <Select size="sm" value={accessMode} onChange={e => setAccessMode(e.target.value)} maxW="420px">
          {opts.email && <option value="email">Specified email addresses only (recommended)</option>}
          {opts.password && <option value="password">Anyone with the link and the portal password</option>}
          {opts.link && <option value="link">Everyone with the public link</option>}
        </Select>
        {accessMode === 'email' && opts.mfa && (
          <Checkbox size="sm" mt={2} isChecked={requireMfa} onChange={e => setRequireMfa(e.target.checked)}>
            <Text fontSize="12px">
              Require email verification code (MFA) — a 4-digit code is emailed and must be entered before uploading. No resend; 10-minute expiry.
            </Text>
          </Checkbox>
        )}
        {accessMode === 'email' && (
          <FormControl mt={2}>
            <FormLabel fontSize="xs">Allowed email addresses (one per line, or comma-separated)</FormLabel>
            <Textarea size="sm" rows={3} value={emails} onChange={e => setEmails(e.target.value)} placeholder={"client@example.com\nsupplier@example.com"} />
          </FormControl>
        )}
        {accessMode === 'password' && (
          <FormControl mt={2} maxW="300px">
            <FormLabel fontSize="xs">Portal password</FormLabel>
            <Input size="sm" type="text" value={password} onChange={e => setPassword(e.target.value)} />
          </FormControl>
        )}
      </Box>

      <Divider />
      <Box>
        <Text fontSize="12px" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={1}>
          Internal visibility (within ForgeBOS)
        </Text>
        <Text fontSize="11px" color="gray.500" mb={2}>
          Control which of your OWN users can see this bucket and open its files. You always retain full access as the creator.
        </Text>
        <Flex gap={4} flexWrap="wrap">
          <FormControl w="280px" isRequired>
            <FormLabel fontSize="xs" mb={0.5}>1. Who can SEE this bucket</FormLabel>
            <Select size="sm" value={visibilityScope} onChange={e => setVisibilityScope(e.target.value)}>
              <option value="all">All tenant users</option>
              <option value="creator">Just me</option>
              <option value="specified">Specified users</option>
            </Select>
          </FormControl>
          <FormControl w="280px" isRequired>
            <FormLabel fontSize="xs" mb={0.5}>2. Who can VIEW &amp; DOWNLOAD files</FormLabel>
            <Select size="sm" value={downloadScope} onChange={e => setDownloadScope(e.target.value)}>
              <option value="all">All tenant users</option>
              <option value="creator">Just me</option>
              <option value="specified">Specified users</option>
            </Select>
          </FormControl>
        </Flex>
        {(visibilityScope === 'specified' || downloadScope === 'specified') && (
          <Box mt={3}>
            <Text fontSize="11px" color="gray.500" mb={1}>Choose the specified users (this list serves both permissions above):</Text>
            <UserTransferList users={users || []} value={internalUsers} onChange={setInternalUsers}
              selfId={selfId} selfName={selfName} />
          </Box>
        )}
      </Box>

      <HStack pt={1}>
        <Button size="sm" colorScheme="teal" onClick={submit} isLoading={busy}
          isDisabled={!name.trim() || (accessMode === 'password' && !password)}>
          Create Portal
        </Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </HStack>
    </VStack>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function UploadPortalsPage({ fullPage, onBack, taskId, subTaskId, contextName }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);           // { has_portals, portals, standard_fields }
  const [details, setDetails] = useState({});     // portalId -> full detail
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [titleMenu, setTitleMenu] = useState(null);   // { x, y, portal }
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [renameDesc, setRenameDesc] = useState('');
  const [reasonVal, setReasonVal] = useState('');
  const [deleteFileTarget, setDeleteFileTarget] = useState(null); // { portal, file }
  const [deleteReason, setDeleteReason] = useState('');
  const [hiddenPortals, setHiddenPortals] = useState({}); // uuid -> true (sidebar show/hide; default all shown)
  const [binFor, setBinFor] = useState(null);     // portalId showing bin
  const [binMenu, setBinMenu] = useState(null);   // { x, y, portal, file }
  const [contactView, setContactView] = useState(null); // known-uploader contact card
  const [accessTarget, setAccessTarget] = useState(null); // 'Edit who can upload'
  const [accessForm, setAccessForm] = useState({ mode: 'email', mfa: false, password: '', vis: 'all', dl: 'all', users: [], reason: '' });
  const openAccessEditor = (p) => {
    const d = details[p.id] || {};
    setAccessForm({
      mode: p.access_mode, mfa: !!p.require_mfa, password: '',
      vis: p.visibility_scope || 'all', dl: p.download_scope || 'all',
      users: (d.internal_users || []).map(u => ({ id: u.id, display_name: u.display_name })),
      reason: '',
    });
    setAccessTarget(p);
  };
  const [undeleteTarget, setUndeleteTarget] = useState(null); // { portal, file }
  const [undeleteReason, setUndeleteReason] = useState('');
  const [bins, setBins] = useState({});
  const [pwTarget, setPwTarget] = useState(null);
  const [pwVal, setPwVal] = useState('');
  const menuRef = useRef(null);

  const qs = subTaskId ? `sub_task_id=${subTaskId}` : `task_id=${taskId}`;
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get('/calendar/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const me = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get(`/upload-portals/context?${qs}`);
      setCtx(data);
      const det = {};
      for (const p of data.portals) {
        try { const r = await api.get(`/upload-portals/${p.id}`); det[p.id] = r.data; } catch (e) {}
      }
      setDetails(det);
    } catch (e) {
      toast({ title: 'Could not load portals', status: 'error', duration: 2500 });
    } finally { setLoading(false); }
  }, [qs, toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const close = () => setTitleMenu(null);
    if (titleMenu) { window.addEventListener('click', close); return () => window.removeEventListener('click', close); }
  }, [titleMenu]);
  useEffect(() => {
    const close = () => setBinMenu(null);
    if (binMenu) { window.addEventListener('click', close); return () => window.removeEventListener('click', close); }
  }, [binMenu]);

  const createPortal = async (payload) => {
    setBusy(true);
    try {
      await api.post('/upload-portals', { ...payload, task_id: taskId, sub_task_id: subTaskId });
      toast({ title: 'Portal created', status: 'success', duration: 1800 });
      setCreating(false);
      await fetchAll();
    } catch (e) {
      toast({ title: e?.response?.data?.error || 'Create failed', status: 'error', duration: 2500 });
    } finally { setBusy(false); }
  };

  const copyLink = async (p) => {
    try { await navigator.clipboard.writeText(p.public_link); toast({ title: 'Public link copied', status: 'success', duration: 1200 }); }
    catch (e) { toast({ title: 'Copy failed', status: 'error', duration: 1500 }); }
  };

  const putPortal = async (p, body, okMsg) => {
    try {
      await api.put(`/upload-portals/${p.id}`, body);
      toast({ title: okMsg, status: 'success', duration: 1500 });
      await fetchAll();
      return true;
    } catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 2500 }); return false; }
  };

  const tokenParam = () => `token=${localStorage.getItem('token')}`;

  const openBin = async (p) => {
    if (binFor === p.id) { setBinFor(null); return; }
    try { const { data } = await api.get(`/upload-portals/${p.id}/recycle-bin`); setBins(b => ({ ...b, [p.id]: data })); setBinFor(p.id); } catch (e) {}
  };

  const title = ctx && !ctx.has_portals && !creating
    ? `Create Public Upload Portal for ${contextName || (subTaskId ? 'Sub-task' : 'Task')}`
    : `Upload Portals — ${contextName || (subTaskId ? 'Sub-task' : 'Task')}`;

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={true} onClose={onBack} title={title}
      headerRight={ctx?.has_portals && !creating ? (
        <Button size="xs" colorScheme="teal" leftIcon={<FiPlus size={11} />} onClick={() => setCreating(true)}>New Portal</Button>
      ) : null}>
      {loading ? <Text fontSize="sm" color="gray.500">Loading…</Text> : (
        <>
          {/* Empty state: invite creation (the form itself lives in the
              80% modal below, for first and subsequent portals alike) */}
          {!ctx?.has_portals && !creating && (
            <Flex direction="column" align="center" py={16} gap={3}>
              <Box color="blue.300"><FiUploadCloud size={44} /></Box>
              <Text fontSize="15px" fontWeight="700">No upload buckets yet for this {subTaskId ? 'sub-task' : 'task'}</Text>
              <Text fontSize="12px" color="gray.500" maxW="560px" textAlign="center">
                A public upload portal gives clients and suppliers a secure, branded page where they can send
                files directly into this {subTaskId ? 'sub-task' : 'task'}. Every upload is attributed to the
                sender's name, email, IP address and browser for audit purposes.
              </Text>
              <Button size="sm" colorScheme="teal" leftIcon={<FiPlus size={12} />} onClick={() => setCreating(true)}>
                Create Public Upload Portal for {contextName || 'this item'}
              </Button>
            </Flex>
          )}

          {/* Create modal — fixed 10% insets = 80% of the screen */}
          <Modal isOpen={creating} onClose={() => setCreating(false)} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent position="fixed" top="10%" bottom="10%" left="10%" right="10%"
              w="auto" maxW="none" h="auto" maxH="none" my={0}
              display="flex" flexDirection="column" overflow="hidden" borderRadius="xl">
              <ModalHeader fontSize="md" borderBottom="1px" borderColor="gray.100" py={3}>
                <HStack spacing={2}>
                  <Box color="blue.400"><FiUploadCloud size={18} /></Box>
                  <Text>Create Public Upload Portal for {contextName || (subTaskId ? 'Sub-task' : 'Task')}</Text>
                </HStack>
              </ModalHeader>
              <ModalBody flex={1} overflowY="auto" py={5} px={8} bg="gray.50">
                <Box bg="white" border="1px" borderColor="gray.200" borderRadius="lg" p={6} maxW="860px" mx="auto">
                  <PortalForm contextLabel={contextName} standardFields={ctx?.standard_fields} securityOptions={ctx?.security_options}
                    users={users} selfId={me?.id} selfName={me?.display_name}
                    onSubmit={createPortal} busy={busy}
                    onCancel={() => setCreating(false)} />
                </Box>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Management: left sidebar (show/hide per bucket, all shown by
              default) + centred, content-fitted cards that wrap side by
              side — no more full-width wasted space. */}
          {ctx?.has_portals && !creating && (
          <Flex align="flex-start" gap={5}>
            <Box w="210px" flexShrink={0} border="1px" borderColor="gray.200" borderRadius="lg" p={3} bg="white">
              <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                Upload Buckets
              </Text>
              <VStack align="stretch" spacing={1}>
                {ctx.portals.map(p => (
                  <Flex key={p.id} align="center" gap={1.5} py={0.5} cursor="pointer" borderRadius="md" px={1}
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => setHiddenPortals(h => ({ ...h, [p.id]: !h[p.id] }))}>
                    <Box color={hiddenPortals[p.id] ? 'gray.300' : 'blue.400'}>
                      {hiddenPortals[p.id] ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                    </Box>
                    <Text fontSize="12px" fontWeight="600" noOfLines={1}
                      color={hiddenPortals[p.id] ? 'gray.400' : 'gray.700'}>{p.name}</Text>
                    {p.is_closed && <FiSlash size={10} color="#e53e3e" />}
                  </Flex>
                ))}
              </VStack>
            </Box>
            <Flex flex={1} gap={4} flexWrap="wrap" justify="center" align="flex-start">
          {ctx.portals.filter(p => !hiddenPortals[p.id]).map(p => {
            const d = details[p.id] || {};
            const files = d.files || [];
            return (
              <Box key={p.id} border="1px" borderColor="gray.300" borderRadius="lg" p={4} bg="white"
                w="fit-content" minW="392px" maxW="100%" boxShadow="sm">
                <Flex align="flex-start" justify="space-between" flexWrap="wrap" gap={2}>
                  <Box onContextMenu={e => { e.preventDefault(); setTitleMenu({ x: e.clientX, y: e.clientY, portal: p }); }}>
                    <HStack spacing={2}>
                      <Box color="gray.700"><FiUploadCloud size={20} strokeWidth={2.5} /></Box>
                      <Text fontSize="15px" fontWeight="800" cursor="context-menu"
                        title="Right-click to rename or close this portal">{p.name}</Text>
                      {p.is_closed
                        ? <Badge colorScheme="red" fontSize="8px"><HStack spacing={1}><FiLock size={8} /><Text>CLOSED TO PUBLIC</Text></HStack></Badge>
                        : <Badge colorScheme="green" fontSize="8px">OPEN</Badge>}
                      <Badge colorScheme="gray" fontSize="8px">
                        {p.access_mode === 'email' ? 'EMAIL LIST' : p.access_mode === 'password' ? 'PASSWORD' : 'PUBLIC LINK'}
                      </Badge>
                      {!!p.require_mfa && <Badge colorScheme="purple" fontSize="8px">MFA</Badge>}
                    </HStack>
                    <Tooltip label="Click to copy the public link">
                      <Text fontSize="10.5px" color="teal.600" fontFamily="mono" mt={0.5} cursor="pointer"
                        onClick={() => copyLink(p)} _hover={{ textDecoration: 'underline' }}>
                        <FiCopy size={9} style={{ display: 'inline', marginRight: 4 }} />{p.public_link}
                      </Text>
                    </Tooltip>
                    {p.description && <Text fontSize="11px" color="gray.500" mt={0.5} maxW="560px">{p.description}</Text>}
                  </Box>
                  <HStack spacing={1}>
                    {p.access_mode === 'password' && (
                      <Tooltip label="Change portal password">
                        <IconButton size="xs" variant="ghost" icon={<FiLock size={12} />} aria-label="password"
                          onClick={() => { setPwTarget(p); setPwVal(''); setReasonVal(''); }} />
                      </Tooltip>
                    )}
                    <Tooltip label={p.is_closed ? 'Public access LOCKED — click to reopen' : 'Lock public access'}>
                      <IconButton size="xs" variant="ghost" icon={<FiSlash size={12} />} aria-label="lock"
                        color={p.is_closed ? 'red.500' : 'gray.300'}
                        _hover={{ color: p.is_closed ? 'red.600' : 'gray.500' }}
                        onClick={() => putPortal(p, {
                          is_closed: !p.is_closed,
                          reason: p.is_closed ? 'Public access unlocked' : 'Public access locked',
                        }, p.is_closed ? 'Portal reopened' : 'Public access locked')} />
                    </Tooltip>
                    <Tooltip label="Open public page">
                      <IconButton size="xs" variant="ghost" icon={<FiExternalLink size={12} />} aria-label="open"
                        onClick={() => window.open(p.public_link, '_blank')} />
                    </Tooltip>
                    {details[p.id]?.my_permissions?.can_download !== false && <Tooltip label="Download all files as zip">
                      <IconButton size="xs" variant="ghost" colorScheme="teal" icon={<FiDownload size={12} />} aria-label="zip"
                        onClick={() => window.open(`/api/upload-portals/${p.id}/download-zip?${tokenParam()}`, '_blank')} />
                    </Tooltip>}
                    <Tooltip label="Recycle bin">
                      <IconButton size="xs" variant="ghost" colorScheme="orange" icon={<FiTrash2 size={12} />} aria-label="bin"
                        onClick={() => openBin(p)} />
                    </Tooltip>
                  </HStack>
                </Flex>

                {/* Allowed emails management */}
                {p.access_mode === 'email' && (
                  <Flex mt={2} gap={1.5} align="center" flexWrap="wrap">
                    <FiMail size={11} color="#718096" />
                    {(d.emails || []).map(em => (
                      <Tag key={em.id} size="sm" fontSize="10px" borderRadius="full">
                        {em.email}
                        <Box as="span" ml={1} cursor="pointer" onClick={async () => {
                          try { await api.delete(`/upload-portals/${p.id}/emails/${em.id}`); fetchAll(); } catch (e) {}
                        }}><FiX size={9} /></Box>
                      </Tag>
                    ))}
                    <Input size="xs" w="200px" placeholder="add email + Enter" onKeyDown={async e => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        const v = e.currentTarget.value.trim(); e.currentTarget.value = '';
                        try { await api.post(`/upload-portals/${p.id}/emails`, { email: v }); fetchAll(); }
                        catch (err) { toast({ title: err?.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
                      }
                    }} />
                  </Flex>
                )}

                {/* File grid */}
                <Flex mt={4} gap={3} flexWrap="wrap" minW="318px" minH="70px" align="flex-start">
                  {files.length === 0 && <Text fontSize="12px" color="gray.400">No files uploaded yet.</Text>}
                  {files.map(f => (
                    <Box key={f.id} w="150px" border="1px" borderColor="gray.100" borderRadius="md" p={2}
                      _hover={{ borderColor: 'teal.200', bg: 'gray.50' }} position="relative">
                      <Flex direction="column" align="center" cursor={d.my_permissions?.can_download === false ? 'default' : 'pointer'}
                        onClick={() => { if (d.my_permissions?.can_download === false) return;
                          window.open(`/api/upload-portals/${p.id}/files/${f.id}/view?${tokenParam()}`, '_blank'); }}>
                        {fileIcon(f.mime_type, f.original_name)}
                        <Text fontSize="10.5px" fontWeight="600" mt={1.5} textAlign="center" noOfLines={2} wordBreak="break-all">
                          {f.original_name}
                        </Text>
                        <Text fontSize="9px" color="gray.400">{((f.size_bytes || 0) / 1024 / 1024).toFixed(2)} MB</Text>
                      </Flex>
                      <HStack spacing={0.5} mt={1.5} justify="center">
                        {d.my_permissions?.can_download !== false && <Tooltip label="Download">
                          <IconButton size="xs" variant="ghost" icon={<FiDownload size={11} />} aria-label="dl"
                            onClick={() => window.open(`/api/upload-portals/${p.id}/files/${f.id}/download?${tokenParam()}`, '_blank')} />
                        </Tooltip>}
                        <Popover placement="auto" strategy="fixed" isLazy>
                          <PopoverTrigger>
                            <IconButton size="xs" variant="ghost" icon={<FiEye size={11} />} aria-label="meta" />
                          </PopoverTrigger>
                          <PopoverContent w="290px" fontSize="11px">
                            <PopoverArrow />
                            <PopoverBody>
                              <VStack align="stretch" spacing={1}>
                                <HStack justify="space-between"><Text fontWeight="700">Uploaded by</Text>{virusBadge(f.virus_status)}</HStack>
                                {f.uploader_contact_id
                                  ? <Tooltip label="Known contact — click for details">
                                      <Text color="teal.600" fontWeight="600" cursor="pointer" _hover={{ textDecoration: 'underline' }}
                                        onClick={async () => {
                                          try {
                                            const { data } = await api.get(`/upload-portals/contact-by-email?email=${encodeURIComponent(f.uploader_email)}`);
                                            setContactView(data);
                                          } catch (e2) { toast({ title: 'Contact not found', status: 'warning', duration: 2000 }); }
                                        }}>
                                        {f.uploader_name} &lt;{f.uploader_email}&gt;
                                      </Text>
                                    </Tooltip>
                                  : <Text>{f.uploader_name} &lt;{f.uploader_email}&gt;</Text>}
                                <Text color="gray.500">IP: {f.uploader_ip || '—'}</Text>
                                <Text color="gray.500" noOfLines={2}>Browser: {f.uploader_user_agent || '—'}</Text>
                                <Text color="gray.500">Received: {new Date(f.created_at).toLocaleString()}</Text>
                                {f.assigned_company_name
                                  ? <Badge colorScheme="blue" fontSize="8px" alignSelf="flex-start">ASSIGNED: {f.assigned_company_name}</Badge>
                                  : <Button size="xs" mt={1} variant="outline" colorScheme="blue" leftIcon={<FiBriefcase size={10} />}
                                      onClick={async () => {
                                        try {
                                          const { data } = await api.post(`/upload-portals/${p.id}/files/${f.id}/assign-company`);
                                          toast({ title: data.message, status: 'success', duration: 2000 });
                                          fetchAll();
                                        } catch (e2) { toast({ title: e2?.response?.data?.error || 'No matching company contact', status: 'warning', duration: 3000 }); }
                                      }}>
                                      Assign to uploader's company
                                    </Button>}
                                {f.metadata && Object.keys(f.metadata).length > 0 && (<>
                                  <Divider my={1} />
                                  <Text fontWeight="700">Information supplied</Text>
                                  {(d.fields || []).filter(fd => f.metadata[fd.field_key] !== undefined && f.metadata[fd.field_key] !== '').map(fd => (
                                    <Text key={fd.field_key}><b>{fd.label}:</b> {String(f.metadata[fd.field_key])}</Text>
                                  ))}
                                </>)}
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                        {d.my_permissions?.can_download !== false && <Tooltip label="Delete">
                          <IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiTrash2 size={11} />} aria-label="del"
                            onClick={() => { setDeleteFileTarget({ portal: p, file: f }); setDeleteReason(''); }} />
                        </Tooltip>}
                      </HStack>
                    </Box>
                  ))}
                </Flex>

                {/* Recycle bin */}
                {binFor === p.id && (
                  <Box mt={4} p={3} bg="orange.50" border="1px" borderColor="orange.100" borderRadius="md">
                    <Text fontSize="11px" fontWeight="800" color="orange.700" textTransform="uppercase" mb={2}>Recycle Bin</Text>
                    {(bins[p.id] || []).length === 0 && <Text fontSize="11px" color="gray.500">Empty.</Text>}
                    {(bins[p.id] || []).map(f => (
                      <Flex key={f.id} align="center" gap={2} py={1} fontSize="11px"
                        color="gray.400" fontStyle="italic" cursor="context-menu"
                        title="Right-click to undelete"
                        onContextMenu={e => { e.preventDefault(); setBinMenu({ x: e.clientX, y: e.clientY, portal: p, file: f }); }}>
                        <Text fontWeight="600" flex={1} noOfLines={1}>{f.original_name}</Text>
                        <Text noOfLines={1}>"{f.delete_reason}"</Text>
                        <Text>{f.deleted_by_name}</Text>
                      </Flex>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
            </Flex>
          </Flex>
          )}
        </>
      )}

      {/* Title right-click context menu */}
      {titleMenu && (
        <Box ref={menuRef} position="fixed" left={titleMenu.x} top={titleMenu.y} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={2000} py={1} minW="190px"
          onClick={e => e.stopPropagation()}>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
            onClick={() => { setRenameTarget(titleMenu.portal); setRenameVal(titleMenu.portal.name); setRenameDesc(titleMenu.portal.description || ''); setReasonVal(''); setTitleMenu(null); }}>
            Rename / Edit Description
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
            onClick={() => { openAccessEditor(titleMenu.portal); setTitleMenu(null); }}>
            Edit who can upload
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: titleMenu.portal.is_closed ? 'green.50' : 'red.50' }}
            color={titleMenu.portal.is_closed ? 'green.600' : 'red.600'} fontWeight="600"
            onClick={async () => {
              const p = titleMenu.portal; setTitleMenu(null);
              await putPortal(p, {
                is_closed: !p.is_closed,
                reason: p.is_closed ? 'Portal reopened to the public' : 'Portal closed to the public',
              }, p.is_closed ? 'Portal reopened' : 'Portal closed to the public');
            }}>
            {titleMenu.portal.is_closed ? <HStack spacing={1}><FiUnlock size={11} /><Text>Reopen Portal</Text></HStack>
              : <HStack spacing={1}><FiLock size={11} /><Text>Close Portal to the public</Text></HStack>}
          </Box>
        </Box>
      )}

      {/* Known-uploader contact details */}
      <Modal isOpen={!!contactView} onClose={() => setContactView(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Contact Details</ModalHeader>
          <ModalBody>
            {contactView && (
              <VStack align="stretch" spacing={2} fontSize="13px">
                <Text fontSize="16px" fontWeight="800">{contactView.first_name} {contactView.last_name}</Text>
                {contactView.job_title && <Text color="gray.600">{contactView.job_title}</Text>}
                {contactView.company_name && (
                  <HStack><FiBriefcase size={13} /><Text fontWeight="600">{contactView.company_name}</Text></HStack>
                )}
                <Divider />
                <HStack><FiMail size={13} /><Text>{contactView.email}</Text></HStack>
                {contactView.phone && <Text>Phone: {contactView.phone}</Text>}
                {contactView.mobile && contactView.mobile !== contactView.phone && <Text>Mobile: {contactView.mobile}</Text>}
                {contactView.notes && (<><Divider /><Text fontSize="12px" color="gray.600" whiteSpace="pre-wrap">{contactView.notes}</Text></>)}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="gray" onClick={() => setContactView(null)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Recycle-bin right-click menu */}
      {binMenu && (
        <Box position="fixed" left={binMenu.x} top={binMenu.y} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={2000} py={1} minW="150px"
          onClick={e => e.stopPropagation()}>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'green.50' }} color="green.600" fontWeight="600"
            onClick={() => { setUndeleteTarget({ portal: binMenu.portal, file: binMenu.file }); setUndeleteReason(''); setBinMenu(null); }}>
            Undelete
          </Box>
        </Box>
      )}

      {/* Undelete — reason mandatory (SITAC restoration) */}
      <Modal isOpen={!!undeleteTarget} onClose={() => setUndeleteTarget(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Undelete File</ModalHeader>
          <ModalBody>
            <Text fontSize="13px" mb={3}>
              Undelete "{undeleteTarget?.file?.original_name}"? Please provide a reason for restoring it.
            </Text>
            <FormControl isRequired><FormLabel fontSize="xs">Reason for undeletion</FormLabel>
              <Input size="sm" value={undeleteReason} onChange={e => setUndeleteReason(e.target.value)} /></FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="gray" onClick={() => setUndeleteTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="green" ml={2} isDisabled={!undeleteReason.trim()}
              onClick={async () => {
                try {
                  await api.post(`/upload-portals/${undeleteTarget.portal.id}/files/${undeleteTarget.file.id}/restore`,
                    { reason: undeleteReason.trim() });
                  setUndeleteTarget(null);
                  const { data } = await api.get(`/upload-portals/${undeleteTarget.portal.id}/recycle-bin`);
                  setBins(b => ({ ...b, [undeleteTarget.portal.id]: data }));
                  fetchAll();
                  toast({ title: 'Restored', status: 'success', duration: 1500 });
                } catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
              }}>UNDELETE</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rename modal (reason required — SITAC) */}
      <Modal isOpen={!!renameTarget} onClose={() => setRenameTarget(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Edit Portal</ModalHeader>
          <ModalBody>
            <FormControl><FormLabel fontSize="xs">Name (shown to the public)</FormLabel>
              <Input size="sm" value={renameVal} onChange={e => setRenameVal(e.target.value)} /></FormControl>
            <FormControl mt={3}><FormLabel fontSize="xs">Friendly description (shown to the public under the title)</FormLabel>
              <Textarea size="sm" rows={2} value={renameDesc} onChange={e => setRenameDesc(e.target.value)} maxLength={500} /></FormControl>
            <FormControl mt={3} isRequired><FormLabel fontSize="xs">Reason for change (recorded in the audit history)</FormLabel>
              <Input size="sm" value={reasonVal} onChange={e => setReasonVal(e.target.value)} /></FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" ml={2} isDisabled={!renameVal.trim() || !reasonVal.trim()}
              onClick={async () => {
                if (await putPortal(renameTarget, { name: renameVal.trim(), description: renameDesc.trim(), reason: reasonVal.trim() }, 'Portal updated')) setRenameTarget(null);
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit who can upload — public access + internal visibility */}
      <Modal isOpen={!!accessTarget} onClose={() => setAccessTarget(null)} size="xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Edit who can upload — {accessTarget?.name}</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
                  Public upload access
                </Text>
                <Select size="sm" value={accessForm.mode} maxW="380px"
                  onChange={e => setAccessForm(f => ({ ...f, mode: e.target.value }))}>
                  {(ctx?.security_options?.email ?? true) && <option value="email">Specified email addresses only</option>}
                  {(ctx?.security_options?.password ?? true) && <option value="password">Anyone with the link and the portal password</option>}
                  {(ctx?.security_options?.link ?? true) && <option value="link">Everyone with the public link</option>}
                </Select>
                {accessForm.mode === 'email' && (ctx?.security_options?.mfa ?? true) && (
                  <Checkbox size="sm" mt={2} isChecked={accessForm.mfa}
                    onChange={e => setAccessForm(f => ({ ...f, mfa: e.target.checked }))}>
                    <Text fontSize="12px">Require email verification code (MFA)</Text>
                  </Checkbox>
                )}
                {accessForm.mode === 'password' && (
                  <FormControl mt={2} maxW="300px">
                    <FormLabel fontSize="xs">New portal password {accessTarget?.has_password ? '(leave blank to keep current)' : ''}</FormLabel>
                    <Input size="sm" value={accessForm.password}
                      onChange={e => setAccessForm(f => ({ ...f, password: e.target.value }))} />
                  </FormControl>
                )}
                {accessForm.mode === 'email' && (
                  <Text fontSize="10px" color="gray.500" mt={1.5}>
                    The allowed email list itself is managed on the bucket card (add with Enter, remove with ×).
                  </Text>
                )}
              </Box>
              <Divider />
              <Box>
                <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
                  Internal visibility (within ForgeBOS)
                </Text>
                <Flex gap={3} flexWrap="wrap">
                  <FormControl w="240px">
                    <FormLabel fontSize="xs" mb={0.5}>1. Who can SEE this bucket</FormLabel>
                    <Select size="sm" value={accessForm.vis} onChange={e => setAccessForm(f => ({ ...f, vis: e.target.value }))}>
                      <option value="all">All tenant users</option>
                      <option value="creator">Just me</option>
                      <option value="specified">Specified users</option>
                    </Select>
                  </FormControl>
                  <FormControl w="240px">
                    <FormLabel fontSize="xs" mb={0.5}>2. Who can VIEW &amp; DOWNLOAD files</FormLabel>
                    <Select size="sm" value={accessForm.dl} onChange={e => setAccessForm(f => ({ ...f, dl: e.target.value }))}>
                      <option value="all">All tenant users</option>
                      <option value="creator">Just me</option>
                      <option value="specified">Specified users</option>
                    </Select>
                  </FormControl>
                </Flex>
                {(accessForm.vis === 'specified' || accessForm.dl === 'specified') && (
                  <Box mt={3}>
                    <UserTransferList users={users} value={accessForm.users}
                      onChange={(v) => setAccessForm(f => ({ ...f, users: v }))}
                      selfId={me?.id} selfName={me?.display_name} />
                  </Box>
                )}
              </Box>
              <Divider />
              <FormControl isRequired>
                <FormLabel fontSize="xs">Reason for change (recorded in the audit history)</FormLabel>
                <Input size="sm" value={accessForm.reason}
                  onChange={e => setAccessForm(f => ({ ...f, reason: e.target.value }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" onClick={() => setAccessTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" ml={2}
              isDisabled={!accessForm.reason.trim() || (accessForm.mode === 'password' && !accessTarget?.has_password && !accessForm.password)}
              onClick={async () => {
                const body = {
                  access_mode: accessForm.mode,
                  require_mfa: accessForm.mode === 'email' ? accessForm.mfa : false,
                  visibility_scope: accessForm.vis, download_scope: accessForm.dl,
                  internal_user_ids: (accessForm.vis === 'specified' || accessForm.dl === 'specified')
                    ? accessForm.users.map(u => u.id) : [],
                  reason: accessForm.reason.trim(),
                };
                if (accessForm.password) body.password = accessForm.password;
                if (await putPortal(accessTarget, body, 'Access updated')) setAccessTarget(null);
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Password change modal (tenant only; reason required) */}
      <Modal isOpen={!!pwTarget} onClose={() => setPwTarget(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Change Portal Password</ModalHeader>
          <ModalBody>
            <FormControl isRequired><FormLabel fontSize="xs">New password</FormLabel>
              <Input size="sm" value={pwVal} onChange={e => setPwVal(e.target.value)} /></FormControl>
            <FormControl mt={3} isRequired><FormLabel fontSize="xs">Reason for change (recorded in the audit history)</FormLabel>
              <Input size="sm" value={reasonVal} onChange={e => setReasonVal(e.target.value)} /></FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" ml={2} isDisabled={!pwVal.trim() || !reasonVal.trim()}
              onClick={async () => {
                if (await putPortal(pwTarget, { password: pwVal, reason: reasonVal.trim() }, 'Password changed')) setPwTarget(null);
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete file — reason mandatory */}
      <Modal isOpen={!!deleteFileTarget} onClose={() => setDeleteFileTarget(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Delete File</ModalHeader>
          <ModalBody>
            <Text fontSize="13px" mb={3}>
              Delete "{deleteFileTarget?.file?.original_name}"? It will move to the recycle bin.
              Please provide a reason for deletion.
            </Text>
            <FormControl isRequired><FormLabel fontSize="xs">Reason for deletion</FormLabel>
              <Input size="sm" value={deleteReason} onChange={e => setDeleteReason(e.target.value)} /></FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="gray" onClick={() => setDeleteFileTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="red" ml={2} isDisabled={!deleteReason.trim()}
              onClick={async () => {
                try {
                  await api.delete(`/upload-portals/${deleteFileTarget.portal.id}/files/${deleteFileTarget.file.id}`,
                    { data: { reason: deleteReason.trim() } });
                  setDeleteFileTarget(null); fetchAll();
                  toast({ title: 'Moved to recycle bin', status: 'info', duration: 1500 });
                } catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
              }}>DELETE</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
}
